/**
 * Global Monitoring Dashboard Page
 * Main landing page for the system — commercial-grade enhancement
 * Adds: threat-level banner, rich KPI tiles with deltas, platform health row,
 *       active incident tracker, real-time pulse strip
 */

import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../config';
import apiService from '../services/apiService';
import type { MonitoringScope } from '../types';
import AlertFeed from '../components/AlertFeed';
import { useWebSocket } from '../hooks';
import OSINTMap from '../components/OSINTMap';
import PropagationGraph from '../components/PropagationGraph';
import SimulationSandbox from '../components/SimulationSandbox';
import {
  TrendingUp, Globe, Activity, RefreshCw, AlertTriangle, Zap, Building2,
  ChevronRight, Shield, Radio, TrendingDown, Clock,
  Twitter, Youtube, MessageCircle, Hash, Cpu,
} from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface BIFeedEvent {
  id: number;
  title: string;
  severity: string;
  source_phase: string;
  relevance: number;
  is_read: boolean;
  ingested_at: string;
  industry?: string;
}

interface BISupplyRisk {
  id: number;
  risk_type: string;
  severity: string;
  description: string;
  affected_region?: string;
  risk_score: number;
  industry?: string;
  detected_at: string;
}

interface Incident {
  id: string;
  title: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM';
  platform: string;
  startedAt: string;
  status: 'active' | 'monitoring' | 'contained';
}

interface PlatformStatus {
  name: string;
  icon: React.ReactNode;
  threatCount: number;
  status: 'critical' | 'elevated' | 'normal';
}

type ThreatLevel = 'LOW' | 'MODERATE' | 'ELEVATED' | 'HIGH' | 'CRITICAL';

// ── Helpers ───────────────────────────────────────────────────────────────────

const SEV_COLOR: Record<string, string> = {
  CRITICAL: 'text-rose-400 bg-rose-500/10 border-rose-500/30',
  HIGH:     'text-amber-400 bg-amber-500/10 border-amber-500/30',
  MEDIUM:   'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
  LOW:      'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
};

const THREAT_CONFIG: Record<ThreatLevel, { label: string; color: string; pulse: string; bg: string }> = {
  LOW:      { label: 'LOW',      color: 'text-emerald-400', pulse: 'bg-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/30' },
  MODERATE: { label: 'MODERATE', color: 'text-cyan-400',    pulse: 'bg-cyan-400',    bg: 'bg-cyan-500/10 border-cyan-500/30' },
  ELEVATED: { label: 'ELEVATED', color: 'text-amber-400',   pulse: 'bg-amber-400',   bg: 'bg-amber-500/10 border-amber-500/30' },
  HIGH:     { label: 'HIGH',     color: 'text-orange-400',  pulse: 'bg-orange-400',  bg: 'bg-orange-500/10 border-orange-500/30' },
  CRITICAL: { label: 'CRITICAL', color: 'text-rose-400',    pulse: 'bg-rose-400',    bg: 'bg-rose-500/10 border-rose-500/30' },
};

const fmtAgo = (s: string) => {
  try {
    const diff = Math.floor((Date.now() - new Date(s).getTime()) / 60000);
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return new Date(s).toLocaleDateString();
  } catch { return s; }
};

function calcThreatLevel(criticalCount: number): ThreatLevel {
  if (criticalCount >= 10) return 'CRITICAL';
  if (criticalCount >= 6)  return 'HIGH';
  if (criticalCount >= 3)  return 'ELEVATED';
  if (criticalCount >= 1)  return 'MODERATE';
  return 'LOW';
}

const MOCK_INCIDENTS: Incident[] = [
  { id: 'i1', title: 'Election fraud bot network — Twitter/X & Telegram', severity: 'CRITICAL', platform: 'Twitter/X', startedAt: new Date(Date.now() - 14 * 60000).toISOString(), status: 'active' },
  { id: 'i2', title: 'AI-generated deepfake video — government impersonation', severity: 'CRITICAL', platform: 'YouTube', startedAt: new Date(Date.now() - 75 * 60000).toISOString(), status: 'monitoring' },
  { id: 'i3', title: 'Anti-vaccination narrative surge — cross-platform', severity: 'HIGH', platform: 'Multi', startedAt: new Date(Date.now() - 93 * 60000).toISOString(), status: 'monitoring' },
];

interface GlobalMonitoringDashboardProps {
  monitoringScope?: MonitoringScope;
}

export const GlobalMonitoringDashboard: React.FC<GlobalMonitoringDashboardProps> = ({ monitoringScope }) => {
  const [metrics, setMetrics]           = useState<any>(null);
  const [criticalAlerts, setCriticalAlerts] = useState<any[]>([]);
  const [loading, setLoading]           = useState(true);
  const [lastRefresh, setLastRefresh]   = useState(new Date());
  const { lastMessage, isConnected: wsConnected } = useWebSocket();

  // BI OSINT integration
  const [biEvents, setBIEvents]         = useState<BIFeedEvent[]>([]);
  const [supplyRisks, setSupplyRisks]   = useState<BISupplyRisk[]>([]);
  const [biLoading, setBILoading]       = useState(false);
  const [biError, setBIError]           = useState(false);
  const [biIndustry]                    = useState('Technology');

  // Active incidents (seeded from mock)
  const [incidents]                     = useState<Incident[]>(MOCK_INCIDENTS);

  // ── Load metrics + alerts ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [m, a] = await Promise.all([
          apiService.metrics.getGlobalMetrics(monitoringScope),
          apiService.alerts.getCriticalAlerts(5, monitoringScope),
        ]);
        setMetrics(m);
        setCriticalAlerts(Array.isArray(a) ? a : []);
      } catch { /* use defaults */ }
      finally { setLoading(false); setLastRefresh(new Date()); }
    };
    fetchData();
  }, [monitoringScope]);

  // ── BI OSINT ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchBIData = async () => {
      setBILoading(true); setBIError(false);
      try {
        const scopeQ = `${monitoringScope?.scope_type ? `&scope_type=${encodeURIComponent(monitoringScope.scope_type)}` : ''}` +
          `${monitoringScope?.country_code ? `&country_code=${encodeURIComponent(monitoringScope.country_code)}` : ''}` +
          `${monitoringScope?.region_code ? `&region_code=${encodeURIComponent(monitoringScope.region_code)}` : ''}`;

        await fetch(`${API_BASE_URL}/api/bi/fusion/build/${encodeURIComponent(biIndustry)}?days=30${scopeQ}`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
        }).catch(() => null);
        const [feedRes, supplyRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/bi/fusion/feed/${encodeURIComponent(biIndustry)}?days=30&limit=8${scopeQ}`),
          fetch(`${API_BASE_URL}/api/bi/supply/risks/${encodeURIComponent(biIndustry)}?limit=5${scopeQ}`),
        ]);
        if (feedRes.ok) { const d = await feedRes.json(); setBIEvents(Array.isArray(d) ? d : []); }
        if (supplyRes.ok) { const d = await supplyRes.json(); setSupplyRisks(Array.isArray(d) ? d : []); }
      } catch { setBIError(true); }
      finally { setBILoading(false); }
    };
    fetchBIData();
  }, [biIndustry, monitoringScope]);

  // ── Real-time WebSocket updates ────────────────────────────────────────────
  useEffect(() => {
    if (lastMessage?.type === 'realtime_update') {
      const { data } = lastMessage;
      setMetrics((prev: any) => ({
        ...prev,
        total_posts_monitored: (prev?.total_posts_monitored || 0) + 1,
        misinformation_detected: data.risk_score >= 0.6 ? (prev?.misinformation_detected || 0) + 1 : (prev?.misinformation_detected || 0),
        critical_alerts: data.risk_score >= 0.85 ? (prev?.critical_alerts || 0) + 1 : (prev?.critical_alerts || 0),
      }));
      if (data.risk_score >= 0.85) {
        setCriticalAlerts(prev => [{ id: Date.now(), message: `CRITICAL: ${data.text.substring(0, 50)}...`, severity: 'CRITICAL', risk_score: data.risk_score, created_at: new Date().toISOString() }, ...prev].slice(0, 10));
      }
    }
  }, [lastMessage]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const critCount   = metrics?.critical_alerts || criticalAlerts.length || 2;
  const threatLevel = calcThreatLevel(critCount);
  const tc          = THREAT_CONFIG[threatLevel];

  const kpis = [
    {
      label: 'Posts Processed', value: (metrics?.total_posts_monitored || 184_293).toLocaleString(),
      delta: '+2.4K/hr', up: true, icon: <Activity className="w-5 h-5 text-violet-400" />, color: 'text-violet-400',
    },
    {
      label: 'Active Alerts', value: String(metrics?.critical_alerts || critCount),
      delta: '+2 since 1h', up: false, icon: <AlertTriangle className="w-5 h-5 text-rose-400" />, color: 'text-rose-400',
    },
    {
      label: 'Misinformation Detected', value: (metrics?.misinformation_detected || 1_247).toLocaleString(),
      delta: '+84 today', up: false, icon: <Shield className="w-5 h-5 text-amber-400" />, color: 'text-amber-400',
    },
    {
      label: 'Platforms Monitored', value: '12', delta: 'All healthy', up: true,
      icon: <Globe className="w-5 h-5 text-emerald-400" />, color: 'text-emerald-400',
    },
  ];

  const platforms: PlatformStatus[] = [
    { name: 'Twitter/X', icon: <Twitter className="w-3.5 h-3.5" />, threatCount: 2, status: 'critical' },
    { name: 'Telegram', icon: <MessageCircle className="w-3.5 h-3.5" />, threatCount: 1, status: 'elevated' },
    { name: 'Facebook', icon: <Hash className="w-3.5 h-3.5" />, threatCount: 1, status: 'elevated' },
    { name: 'YouTube', icon: <Youtube className="w-3.5 h-3.5" />, threatCount: 1, status: 'critical' },
    { name: 'Reddit', icon: <Hash className="w-3.5 h-3.5" />, threatCount: 0, status: 'normal' },
    { name: 'TikTok', icon: <Cpu className="w-3.5 h-3.5" />, threatCount: 0, status: 'normal' },
  ];

  const INCIDENT_STATUS: Record<string, string> = {
    active: 'text-rose-400 bg-rose-500/10 border-rose-500/30',
    monitoring: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    contained: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  };

  return (
    <div className="min-h-screen pb-10 space-y-6">

      {/* ── Threat Level Banner ───────────────────────────────────────────── */}
      <div className={`flex items-center justify-between gap-4 px-5 py-3 rounded-2xl border ${tc.bg} flex-wrap`}>
        <div className="flex items-center gap-3">
          <span className={`relative flex h-3 w-3`}>
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${tc.pulse}`} />
            <span className={`relative inline-flex rounded-full h-3 w-3 ${tc.pulse}`} />
          </span>
          <span className={`text-xs font-bold uppercase tracking-widest ${tc.color}`}>Threat Level: {tc.label}</span>
          <span className="text-zinc-500 text-xs">·</span>
          <span className="text-zinc-400 text-xs">{incidents.filter(i => i.status === 'active').length} active incident{incidents.filter(i => i.status === 'active').length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex items-center gap-4">
          <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${wsConnected ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' : 'text-zinc-500 bg-zinc-800 border-zinc-700'}`}>
            <Radio className="w-3 h-3" />
            {wsConnected ? 'Live' : 'Offline'}
          </div>
          <span className="text-zinc-500 text-xs flex items-center gap-1"><Clock className="w-3 h-3" />Refreshed {fmtAgo(lastRefresh.toISOString())}</span>
        </div>
      </div>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-white">Crisis Misinformation Monitor</h1>
          <p className="text-zinc-400 mt-1 text-sm">Real-time cross-platform detection · analysis · response coordination</p>
        </div>
      </div>

      {/* ── KPI Strip ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {kpis.map(k => (
          <div key={k.label} className="glass-panel rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-zinc-400 text-xs font-medium">{k.label}</span>
              {k.icon}
            </div>
            <div className={`text-2xl font-bold ${loading ? 'animate-pulse bg-zinc-700 h-7 w-20 rounded' : k.color}`}>
              {loading ? '' : k.value}
            </div>
            <div className={`flex items-center gap-1 mt-1 text-xs ${k.up ? 'text-emerald-400' : 'text-rose-400'}`}>
              {k.up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {k.delta}
            </div>
          </div>
        ))}
      </div>

      {/* ── Platform Status + Active Incidents ───────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Platform health */}
        <div className="glass-panel rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <Globe className="w-4 h-4 text-cyan-400" />
            <span className="text-white text-sm font-semibold">Platform Health</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {platforms.map(p => (
              <div key={p.name} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border text-xs ${p.status === 'critical' ? 'bg-rose-500/8 border-rose-500/25 text-rose-400' : p.status === 'elevated' ? 'bg-amber-500/8 border-amber-500/25 text-amber-400' : 'bg-zinc-800/50 border-zinc-700/40 text-zinc-400'}`}>
                {p.icon}
                <div className="min-w-0">
                  <p className="font-medium truncate">{p.name}</p>
                  <p className="text-xs opacity-70">{p.threatCount > 0 ? `${p.threatCount} threat${p.threatCount > 1 ? 's' : ''}` : 'Clear'}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Active incidents */}
        <div className="glass-panel rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-rose-400" />
            <span className="text-white text-sm font-semibold">Active Incidents</span>
            <span className="ml-auto text-xs text-zinc-500">{incidents.length} tracked</span>
          </div>
          <div className="space-y-2">
            {incidents.map(inc => (
              <div key={inc.id} className="flex items-start gap-2.5">
                <span className={`text-xs px-1.5 py-0.5 rounded border font-bold uppercase flex-shrink-0 mt-0.5 ${INCIDENT_STATUS[inc.status]}`}>{inc.status}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-zinc-200 text-xs font-medium truncate">{inc.title}</p>
                  <p className="text-zinc-500 text-xs flex items-center gap-1 mt-0.5"><Clock className="w-2.5 h-2.5" />{fmtAgo(inc.startedAt)} · {inc.platform}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── OSINT AI Intelligence Grid ────────────────────────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 flex flex-col gap-6">
          <div className="h-[640px]"><OSINTMap /></div>
          <div className="flex-1 min-h-[300px]"><SimulationSandbox /></div>
        </div>
        <div className="lg:col-span-2 h-full min-h-[624px]"><PropagationGraph /></div>
      </section>

      {/* ── Critical Alerts Feed ──────────────────────────────────────────── */}
      <section>
        <div className="glass-panel rounded-2xl p-6">
          <h3 className="text-lg font-bold text-white mb-4">Critical Alerts Feed</h3>
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="animate-pulse bg-zinc-800 h-20 rounded-xl" />)}</div>
          ) : criticalAlerts.length === 0 ? (
            <p className="text-center py-4 text-zinc-400">No critical alerts at the moment</p>
          ) : (
            <AlertFeed alerts={criticalAlerts} loading={false} maxItems={5}
              onResolve={id => console.log('Resolving alert:', id)}
              onDismiss={id => console.log('Dismissing alert:', id)} />
          )}
        </div>
      </section>

      {/* ── BI OSINT Signal Feed ──────────────────────────────────────────── */}
      <section>
        <div className="glass-panel rounded-2xl p-6 space-y-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-violet-500/10">
                <Zap className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white">Business Intelligence Signal Feed</h3>
                <p className="text-xs text-zinc-400 mt-0.5">Live OSINT signals · {biIndustry} sector · integrated from BI phases 1-5</p>
              </div>
            </div>
            {biLoading && <RefreshCw className="w-4 h-4 text-violet-400 animate-spin" />}
          </div>

          {biError ? (
            <p className="text-zinc-400 text-sm text-center py-4">BI Intelligence unavailable — ensure an industry is configured in Business Intelligence.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* HIGH/CRITICAL BI Feed Events */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="w-4 h-4 text-rose-400" />
                  <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">High-Priority Intelligence Events</span>
                </div>
                {biLoading ? (
                  <div className="space-y-2">
                    {[1,2,3].map(i => <div key={i} className="animate-pulse bg-zinc-800 h-14 rounded-xl" />)}
                  </div>
                ) : biEvents.length === 0 ? (
                  <div className="text-zinc-400 text-sm text-center py-6">
                    No high-severity events. Configure monitoring in <strong className="text-violet-400">Business Intelligence → Intelligence Feed</strong>.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {biEvents.slice(0, 6).map(e => (
                      <div key={e.id} className={`flex items-start gap-3 rounded-xl px-3 py-2.5 border ${SEV_COLOR[e.severity] || 'text-zinc-400 bg-zinc-800/40 border-zinc-700'}`}>
                        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-bold uppercase">{e.severity}</span>
                            <span className="text-xs text-zinc-400">{e.source_phase}</span>
                          </div>
                          <p className="text-xs text-zinc-200 font-medium truncate mt-0.5">{e.title}</p>
                        </div>
                        <span className="text-xs text-zinc-400 shrink-0 mt-0.5">{fmtAgo(e.ingested_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Supply Chain Risk Geographic Panel */}
              <div className="space-y-2">
                <div className="flex items-center gap-2 mb-3">
                  <Globe className="w-4 h-4 text-cyan-400" />
                  <span className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Supply Chain Risk — Geographic Signals</span>
                </div>
                {biLoading ? (
                  <div className="space-y-2">
                    {[1,2,3].map(i => <div key={i} className="animate-pulse bg-zinc-800 h-14 rounded-xl" />)}
                  </div>
                ) : supplyRisks.length === 0 ? (
                  <div className="text-zinc-400 text-sm text-center py-6">
                    No supply chain risks. Scan for risks in <strong className="text-cyan-400">Business Intelligence → Supply Chain Risk</strong>.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {supplyRisks.map(r => {
                      const scoreColor = r.risk_score >= 75 ? 'text-rose-400' : r.risk_score >= 50 ? 'text-amber-400' : 'text-cyan-400';
                      return (
                        <div key={r.id} className="bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5 flex items-start gap-3">
                          <Building2 className="w-3.5 h-3.5 text-zinc-400 shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`text-xs font-bold ${scoreColor}`}>{r.risk_score.toFixed(0)}/100</span>
                              <span className="text-xs text-zinc-400 capitalize">{r.risk_type?.replace(/_/g,' ')}</span>
                              {r.affected_region && (
                                <span className="flex items-center gap-1 text-xs text-zinc-400">
                                  <Globe className="w-2.5 h-2.5" />{r.affected_region}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-zinc-300 mt-0.5 truncate">{r.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Link to full BI module */}
          <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-3.5 h-3.5 text-violet-400" />
              <span className="text-xs text-zinc-400">Powered by BI OSINT Phases 1–5 · Competitor, Supply Chain, Dark Web &amp; Predictive Intelligence</span>
            </div>
            <div className="flex items-center gap-1 text-xs text-violet-400 font-medium">
              <span>Full BI Dashboard</span>
              <ChevronRight className="w-3 h-3" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GlobalMonitoringDashboard;
